import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import PropTypes from "prop-types";
import { Dialog } from "primereact/dialog";
import { SelectButton } from "primereact/selectbutton";
import { InputText, Label } from "../../../../components/Inputs";
import {
  EditIconButton,
  FormMenu,
  PrimaryButton,
} from "../../../../components/Buttons";
import {
  validateEmail,
  validateTelNumber,
  validateName,
  STATUS_IDS,
  STATUS_LABELS,
} from "../../../../components/Utils";
import { connect } from "react-redux";
import { saveWorkerEmail } from "../../actions";

const defaultModel = {
  firstname: "",
  lastname: "",
  email: "",
  cellNumber: "",
  telephoneNumber: "",
  username: "",
  password: "**********",
  preferredContactMethod: "email", // 'cell_number'
};

const getInitialFormState = (model /* , userChecked, isNGUMActive */) => {
  const m = model || {};
  return {
    firstname: m.firstname || "",
    lastname: m.lastname || "",
    email: m.email || "",
    newEmail: m.newEmail || m.email || "",
    cellNumber: m.cellNumber || "",
    telephoneNumber: m.telephoneNumber || "",
    preferredContactMethod: m.preferredContactMethod || "email", // 'cell_number'
    error: {
      firstname: null,
      lastname: null,
      email: null,
      telephoneNumber: null,
      // telephoneNumber: (userChecked === null || userChecked === true) ? null : undefined,
    },
  };
};

const validateUserName = (name, label) => {
  if (name.trim() === "") {
    return `${label} darf nicht leer sein`;
  }
  // contains only letters (and numbers in testing mode)
  if (
    window.TESTING === "YES"
      ? !/^[A-Za-z\s-\d]+$/.test(String(name).trim())
      : !validateName(name)
  ) {
    return `${label} ist ungültig (Umlaute/Sonderzeichen entfernen)`;
  }
  return null;
};

const validateInput = {
  firstname: (value) => validateUserName(value, "Vorname"),
  lastname: (value) => validateUserName(value, "Nachname"),
  email: (value) => {
    if (value.trim() === "") {
      return `E-Mail darf nicht leer sein`;
    }
    // contains only letters
    if (!validateEmail(value)) {
      return `E-Mail ist ungültig (Umlaute/Sonderzeichen entfernen)`;
    }
    return null;
  },
  telephoneNumber: (value) => {
    if (value.trim() === "") {
      return `Telefonnummer darf nicht leer sein`;
    }
    if (!validateTelNumber(value)) {
      return "Telefonnummer ist ungültig";
    }
    return null;
  },
  default: (value) =>
    value.trim() === "" ? "Dieses Feld darf nicht leer sein" : null,
};

const CheckDataForm = forwardRef(function CheckDataForm(props, ref) {
  const {
    className = "",
    model = defaultModel,
    userChecked,
    userExists,
    disabled,
    // checkUser,
    // isNGUMActive,
  } = props;

  const [formState, setFormState] = useState(() =>
    getInitialFormState(model, userChecked /* , isNGUMActive */),
  );

  // Keep refs current every render so imperative handles never read stale values
  const formStateRef = useRef(formState);
  const propsRef = useRef(props);
  formStateRef.current = formState;
  propsRef.current = props;

  // Reset form when the loaded model identity, NGUM status, or user-check status changes
  useEffect(() => {
    setFormState(
      getInitialFormState(
        propsRef.current.model,
        propsRef.current.userChecked,
        // propsRef.current.isNGUMActive,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model ? model.id : undefined, userChecked /* , isNGUMActive */]);

  const onChange = (key, value) => {
    setFormState((prev) => {
      const error = { ...prev.error };
      if (error[key] !== undefined) {
        // error[key] = value === "" ? true : null;
        error[key] = validateInput[key]
          ? validateInput[key](value)
          : validateInput.default(value);
      }
      return { ...prev, [key]: value, error };
    });
  };

  // Reads latest values via refs — safe to capture once in useImperativeHandle
  const validate = (noValidate) => {
    const { error: currentError, ...values } = formStateRef.current;
    const error = { ...currentError };
    let isValid = true;

    const keys = Object.keys(values);
    for (const key of keys) {
      // validate firstname, lastname, and email only if user does not exist (userExists === false)
      if (
        (key === "firstname" || key === "lastname" || key === "email") &&
        propsRef.current.userExists
      ) {
        continue;
      }
      if (error[key] !== undefined) {
        const validationError = validateInput[key]
          ? validateInput[key](values[key])
          : validateInput.default(values[key]);
        error[key] = validationError;
        isValid = isValid && validationError === null;
      }
    }
    // Object.keys(values).forEach(key => {
    //   if (error[key] !== undefined) {
    //     const validationError = validateInput[key] ? validateInput[key](values[key]) : validateInput.default(values[key]);
    //     error[key] = validationError;
    //     isValid = isValid && validationError === null;
    //   }
    // });

    if (!noValidate) {
      setFormState((prev) => ({ ...prev, error }));
      if (!isValid) return null;
    }

    values.username = propsRef.current.model
      ? propsRef.current.model.username
      : undefined;
    values.password = propsRef.current.model
      ? propsRef.current.model.password
      : undefined;
    return values;
  };

  useImperativeHandle(
    ref,
    () => ({
      getModel: (noValidate) => validate(noValidate),
    }),
    [],
  ); // empty deps — validate reads latest values via refs

  const handleCheckUser = () => {
    const model = validate();
    if (!model) return;
    propsRef.current.checkUser && propsRef.current.checkUser(model);
  };

  if (userChecked !== false) {
    const { newEmail, ...restModel } = props.model || {};
    const enhancedModel = {
      ...restModel,
      newEmail,
      email_status_id: newEmail ? STATUS_IDS.PROCESSING_UPDATE : undefined,
    };
    return (
      <CheckedDataComponent
        state={formState}
        props={{ ...props, model: enhancedModel }}
        onChange={onChange}
      />
    );
  }

  const { firstname, lastname, email, telephoneNumber, error } = formState;
  const isValid =
    !error.firstname &&
    !error.lastname &&
    !error.email &&
    !error.telephoneNumber;

  return (
    <div className={`${className} check-data-form`}>
      <div className="w3-row neg-margin-lr">
        <div
          className={`w3-col s4 pad-lr pad-top form-group-item ${error.lastname ? " error-group" : ""}`}
        >
          <Label htmlFor="lastname">
            {`Nachname`}
            <span>*</span>
          </Label>
          <InputText
            id="lastname"
            className="w3-block w3-border"
            value={lastname}
            disabled={disabled || userExists}
            onChange={(event) => onChange("lastname", event.target.value)}
          />
          {error.lastname && (
            <span className="error-label pad-sm-top">{error.lastname}</span>
          )}
        </div>
        <div
          className={`w3-col s4 pad-lr pad-top form-group-item ${error.firstname ? " error-group" : ""}`}
        >
          <Label htmlFor="firstname">
            {`Vorname`}
            <span>*</span>
          </Label>
          <InputText
            id="firstname"
            className="w3-block w3-border"
            value={firstname}
            disabled={disabled || userExists}
            onChange={(event) => onChange("firstname", event.target.value)}
          />
          {error.firstname && (
            <div className="error-label pad-sm-top">{error.firstname}</div>
          )}
        </div>
      </div>
      <div className="w3-row neg-margin-lr">
        <div
          className={`w3-col s4 pad-lr pad-top form-group-item ${error.email ? " error-group" : ""}`}
        >
          <Label htmlFor="email">
            {`E-Mail`}
            <span>*</span>
          </Label>
          <InputText
            id="email"
            className="w3-block w3-border"
            value={email}
            type="email"
            disabled={disabled}
            onChange={(event) => onChange("email", event.target.value)}
          />
          {error.email && <div className="pad-sm-top">{error.email}</div>}
        </div>
        <div
          className={`w3-col s4 pad-lr pad-top form-group-item ${error.telephoneNumber ? "error-group" : ""}`}
        >
          <Label htmlFor="telephoneNumber">
            {`Telefon (IT Hotline Authentifizierung)`}
            <span>*</span>
          </Label>
          <InputText
            id="telephoneNumber"
            className="w3-block w3-border"
            value={telephoneNumber}
            placeholder="+49177123457"
            disabled={disabled}
            onChange={(event) =>
              onChange("telephoneNumber", event.target.value)
            }
          />
          {error.telephoneNumber && (
            <div className="pad-sm-top">{error.telephoneNumber}</div>
          )}
        </div>
        <div className="w3-col s4 pad-lr pad-top form-group-item ">
          <Label>&nbsp;</Label>
          <PrimaryButton
            className=""
            id="checkWorkerBtn"
            name="checkWorkerBtn"
            onClick={handleCheckUser}
            disabled={!isValid}
          >
            {"Prüfen"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
});

CheckDataForm.propTypes = {
  className: PropTypes.string,
  model: PropTypes.object,
  userChecked: PropTypes.bool,
  userExists: PropTypes.bool,
  disabled: PropTypes.bool,
  checkUser: PropTypes.func,
  isNGUMActive: PropTypes.bool,
};

export default CheckDataForm;

function CheckedDataComponent({ state, props, onChange }) {
  const {
    className,
    userExists,
    model: { username, password },
    disabled,
  } = props;
  const { firstname, lastname, telephoneNumber, error } = state;

  return (
    <div className={`${className} check-data-form`}>
      <div className="w3-row neg-margin-lr">
        <div
          className={`w3-col s4 pad-lr pad-top form-group-item ${error.lastname ? " error-group" : ""}`}
        >
          <Label htmlFor="lastname">
            {`Nachname`}
            <span>*</span>
          </Label>
          <InputText
            id="lastname"
            className="w3-block w3-border"
            value={lastname}
            disabled={disabled || userExists}
            onChange={(event) => onChange("lastname", event.target.value)}
          />
          {error.lastname && (
            <div className="error-label pad-sm-top">{error.lastname}</div>
          )}
        </div>
        <div
          className={`w3-col s4 pad-lr pad-top form-group-item ${error.firstname ? " error-group" : ""}`}
        >
          <Label htmlFor="firstname">
            {`Vorname`}
            <span>*</span>
          </Label>
          <InputText
            id="firstname"
            className="w3-block w3-border"
            value={firstname}
            disabled={disabled || userExists}
            onChange={(event) => onChange("firstname", event.target.value)}
          />
          {error.firstname && (
            <div className="error-label pad-sm-top">{error.firstname}</div>
          )}
        </div>
        {
          /* isNGUMActive &&  */ props.model.id ? (
            <EmailInputPanel state={state} props={props} onChange={onChange} disabled={disabled} />
          ) : (
            <div
              className={`w3-col s4 pad-lr pad-top form-group-item ${error.email ? " error-group" : ""}`}
            >
              <Label htmlFor="email">
                {`E-Mail`}
                <span>*</span>
              </Label>
              <InputText
                id="email"
                className="w3-block w3-border"
                value={state.email}
                type="email"
                disabled={disabled}
                onChange={(event) => onChange("email", event.target.value)}
              />
              {error.email && (
                <div className="error-label pad-sm-top">{error.email}</div>
              )}
            </div>
          )
        }
      </div>
      <div className="w3-row neg-margin-lr">
        <div className="w3-col s4 pad-lr pad-top">
          <Label>{`Mitarbeiterprofil`}</Label>
          <InputText className="w3-block w3-border" value={username} disabled />
        </div>
        <div className="w3-col s4 pad-lr pad-top">
          <Label>{`Passwort`}</Label>
          <InputText
            type="password"
            className="w3-block w3-border"
            value={password || "**********"}
            disabled
          />
        </div>
        <div
          className={`w3-col s4 pad-lr pad-top form-group-item ${error.telephoneNumber ? "error-group" : ""}`}
        >
          <Label htmlFor="telephoneNumber">
            {/* {`Telefon (dienstlich)`} */}
            {"Telefon (IT Hotline Authentifizierung)"}
            <span>*</span>
          </Label>
          <InputText
            id="telephoneNumber"
            className="w3-block w3-border"
            value={telephoneNumber}
            placeholder="+49177123457"
            disabled={disabled}
            onChange={(event) =>
              onChange("telephoneNumber", event.target.value)
            }
          />
          {error.telephoneNumber && (
            <div className="error-label pad-sm-top">
              {error.telephoneNumber}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const mapDispatchToProps = (dispatch) => ({
  changeEmail: (...args) => dispatch(saveWorkerEmail(...args)),
});

function EmailInputPanelComponent({ state, props, changeEmail, disabled }) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(state.newEmail);
  const [error, setError] = useState(null);
  const {
    isNGUMActive,
    model: { email_status_id },
  } = props;

  const visibilityOptions = [
    STATUS_IDS.NEW,
    STATUS_IDS.PROCESSING,
    STATUS_IDS.PROCESSING_UPDATE,
  ];
  const changeTaskExists =
    email_status_id && visibilityOptions.includes(email_status_id);
  console.log(
    "EmailInputPanelComponent()",
    props.model,
    visibilityOptions,
    changeTaskExists,
    state,
  );

  const handleChange = (newValue) => {
    // let newError = null;
    // if (!validateEmail(newValue)) {
    //   newError = "E-Mail ist ungültig (Umlaute/Sonderzeichen entfernen)";
    // }
    const newError = validateInput.email(newValue);
    setValue(newValue);
    setError(newError);
  };

  const onSave = () => {
    console.log("saving changes to Email ... ", value);
    setIsEditing(false);
    changeEmail({ id: props.model.id, newEmail: value, email: state.email });
  };

  const editBtnMarkup = /* isNGUMActive &&  */ props.model.id && (
    <div
      className="pad-sm-top pad-sm-rht"
      style={{ position: "absolute", top: "0px", right: "0px" }}
    >
      <EditIconButton
        className="pad-icon-sm"
        disabled={disabled}
        onClick={() => setIsEditing(true)}
      />
    </div>
  );
  const dialogMarkup = /* isNGUMActive &&  */ isEditing && (
    <Dialog
      // header={(<div style={{margin: '-1em', padding: '1em'}}>{`E-Mail bearbeiten`}</div>)}
      header={<h3 className="no-margin">{`E-Mail bearbeiten`}</h3>}
      visible
      style={{ width: "400px" }}
      modal
      onHide={() => setIsEditing(false)}
    >
      <div className="pos-rel" style={{ minHeight: "165px" }}>
        <div className={`form-group-item ${error ? " error-group" : ""}`}>
          <Label htmlFor="email">
            {`E-Mail`}
            <span>*</span>
          </Label>
          <InputText
            id="email"
            className="w3-block w3-border"
            value={value}
            type="email"
            onChange={(event) => handleChange(event.target.value)}
          />
          {error && <div className="error-label pad-sm-top">{error}</div>}
        </div>
        <div
          className="pad-sm-top"
          style={{
            position: "absolute",
            bottom: "0",
            left: "0",
            width: "100%",
          }}
        >
          <Label className="w3-block no-margin-btm">{"Hinweis:"}</Label>
          <div>{`Diese E-Mail wird erst nach Bestätigung durch Vodafone übernommen`}</div>
        </div>
      </div>
      <FormMenu
        className="margin-top w3-border-top pad-big w3-light-grey"
        style={{
          marginLeft: "-1em",
          marginRight: "-1em",
          marginBottom: "-1em",
        }}
        onSave={onSave}
        onCancel={() => setIsEditing(false)}
      />
    </Dialog>
  );

  const statusLabel = /* isNGUMActive &&  */ changeTaskExists && (
    <div className="pos-rel" style={{ height: "22px" }}>
      <span
        className="inline pad-sm-tb pad-lr w3-light-grey pos-rel"
        style={{ top: "-8px" }}
        title={value}
      >
        <Label className="inline bold-">
          {STATUS_LABELS[email_status_id] || "In Beauftragung"}
        </Label>
      </span>
    </div>
  );

  return (
    <div className={`w3-col s4 pad-lr pad-top form-group-item`}>
      <div
        className="pos-rel"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <Label className="inline" htmlFor="email">
          {`E-Mail`}
          <span>*</span>
        </Label>
        {statusLabel}
      </div>
      <div className="pos-rel">
        <InputText
          id="email"
          className="w3-block w3-border"
          value={state.email}
          type="email"
          disabled /* ={isNGUMActive} */
        />
        {editBtnMarkup}
      </div>
      {dialogMarkup}
    </div>
  );
}

const EmailInputPanel = connect(
  null,
  mapDispatchToProps,
)(EmailInputPanelComponent);
